<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import { api, type BinnedDocument } from '../lib/api';
  import { userSettings } from '../lib/stores/settingsStore';
  import { toastStore } from '../lib/stores/toastStore';
  import { formatDate } from '../lib/settings';
  import { isSelectionClearKey } from '../lib/selectionShortcut';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import {
    Button,
    Spinner,
    EmptyState,
    FilterToolbar,
    TableSortHeader,
  } from '../lib/components/ui/index';
  import type { FilterDef, FilterValue } from '../lib/components/ui/FilterToolbar.svelte';

  // Client-side filter state over the seven E3 fields. Filtering/sorting stay entirely in the
  // browser — the endpoint always returns the full (capped) bin listing (Spec 073/E3).
  interface BinFilterState {
    subject?: string | null;
    merchant?: string | null;
    category?: string | null;
    referenceDate?: { from?: string | null; to?: string | null } | null;
    uploadedAt?: { from?: string | null; to?: string | null } | null;
    deletedAt?: { from?: string | null; to?: string | null } | null;
    amount?: { min?: number | null; max?: number | null } | null;
  }

  let rawDocuments: BinnedDocument[] = [];
  let loading = true;
  let error: string | null = null;
  let hasMore = false;

  let filterState: BinFilterState = {};
  let sortBy: string | null = 'deletedAt';
  let sortOrder: 'asc' | 'desc' | null = 'desc';
  let selectedIds: Set<string> = new Set();

  async function load() {
    loading = true;
    error = null;
    try {
      const response = await api.documents.getBin();
      rawDocuments = response.items;
      hasMore = response.pagination.hasMore;
      selectedIds = new Set();
    } catch (err) {
      error = err instanceof Error ? err.message : $_('pages.bin.load_error');
      toastStore.error($_('pages.bin.load_error'));
    } finally {
      loading = false;
    }
  }

  onMount(load);

  // --- Filtering (E3, E4) ---

  function matchesText(value: string | undefined, filter: string | null | undefined): boolean {
    if (!filter) return true;
    return (value ?? '').toLowerCase().includes(filter.toLowerCase());
  }

  function matchesDateRange(
    value: string | undefined,
    range: { from?: string | null; to?: string | null } | null | undefined,
  ): boolean {
    if (!range || (!range.from && !range.to)) return true;
    if (!value) return false;
    const t = new Date(value).getTime();
    if (range.from && t < new Date(range.from).getTime()) return false;
    if (range.to && t > new Date(range.to).getTime() + 24 * 60 * 60 * 1000 - 1) return false;
    return true;
  }

  function matchesNumberRange(
    value: number | undefined,
    range: { min?: number | null; max?: number | null } | null | undefined,
  ): boolean {
    if (!range || (range.min == null && range.max == null)) return true;
    if (value == null) return false;
    if (range.min != null && value < range.min) return false;
    if (range.max != null && value > range.max) return false;
    return true;
  }

  function applyFilters(docs: BinnedDocument[], f: BinFilterState): BinnedDocument[] {
    return docs.filter(
      (d) =>
        matchesText(d.subject, f.subject) &&
        matchesText(d.merchant, f.merchant) &&
        matchesText(d.category, f.category) &&
        matchesDateRange(d.referenceDate, f.referenceDate) &&
        matchesDateRange(d.uploadedAt, f.uploadedAt) &&
        matchesDateRange(d.deletedAt, f.deletedAt) &&
        matchesNumberRange(d.amount, f.amount),
    );
  }

  // --- Sorting (E3) ---

  function sortValue(doc: BinnedDocument, key: string): string | number | null {
    switch (key) {
      case 'referenceDate':
      case 'uploadedAt':
      case 'deletedAt': {
        const v = doc[key as 'referenceDate' | 'uploadedAt' | 'deletedAt'];
        return v ? new Date(v).getTime() : null;
      }
      case 'amount':
        return doc.amount ?? null;
      case 'subject':
        return doc.subject?.toLowerCase() ?? null;
      case 'merchant':
        return doc.merchant?.toLowerCase() ?? null;
      case 'category':
        return doc.category?.toLowerCase() ?? null;
      default:
        return null;
    }
  }

  function sortDocs(
    docs: BinnedDocument[],
    key: string | null,
    order: 'asc' | 'desc' | null,
  ): BinnedDocument[] {
    if (!key || !order) return docs;
    const factor = order === 'asc' ? 1 : -1;
    return [...docs].sort((a, b) => {
      const av = sortValue(a, key);
      const bv = sortValue(b, key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last, regardless of direction
      if (bv == null) return -1;
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return 0;
    });
  }

  $: filteredDocuments = applyFilters(rawDocuments, filterState);
  $: sortedDocuments = sortDocs(filteredDocuments, sortBy, sortOrder);
  $: allVisibleSelected =
    sortedDocuments.length > 0 && sortedDocuments.every((d) => selectedIds.has(d.id));

  $: availableFilters = [
    { key: 'subject', label: $_('pages.bin.column_subject'), type: 'text' },
    { key: 'merchant', label: $_('pages.bin.column_merchant'), type: 'text' },
    { key: 'category', label: $_('pages.bin.column_category'), type: 'text' },
    { key: 'referenceDate', label: $_('pages.bin.column_reference_date'), type: 'date-range' },
    { key: 'uploadedAt', label: $_('pages.bin.column_uploaded_at'), type: 'date-range' },
    { key: 'deletedAt', label: $_('pages.bin.column_deleted_at'), type: 'date-range' },
    { key: 'amount', label: $_('pages.bin.column_amount'), type: 'number-range' },
  ] satisfies FilterDef[];

  function handleFilterChange(e: CustomEvent<{ key: string; value: FilterValue }>) {
    filterState = { ...filterState, [e.detail.key]: e.detail.value } as BinFilterState;
  }

  function handleFilterRemove(e: CustomEvent<{ key: string }>) {
    filterState = { ...filterState, [e.detail.key]: null } as BinFilterState;
  }

  function handleClearAll() {
    filterState = {};
  }

  function handleSort(e: CustomEvent<{ key: string; order: 'asc' | 'desc' | null }>) {
    sortBy = e.detail.key;
    sortOrder = e.detail.order;
  }

  // --- Multiselect (E4, same additive-on-visible pattern as iter-15/#295) ---

  function handleRowToggle(id: string, e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    selectedIds = next;
  }

  function handleSelectAll(e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    const next = new Set(selectedIds);
    for (const doc of sortedDocuments) {
      if (checked) next.add(doc.id);
      else next.delete(doc.id);
    }
    selectedIds = next;
  }

  // Spec 103 E9: Esc clears the selection (no button any more), but not from a text field.
  function handleWindowKeydown(e: KeyboardEvent) {
    if (selectedIds.size > 0 && isSelectionClearKey(e)) selectedIds = new Set();
  }

  function getDocumentUrl(doc: BinnedDocument): string {
    // Never the slug (E6a): a slug is resolved through the catalog, which a binned document has
    // just been removed from — that would 404.
    return `/documents/${doc.id}`;
  }

  function handleRowLinkClick(e: MouseEvent, doc: BinnedDocument) {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    navigate(getDocumentUrl(doc));
  }

  // --- Restore (E2, E5) ---

  async function handleRestore() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      const result = await api.documents.restoreDocuments(ids);
      selectedIds = new Set();
      if (result.restoredCount === result.totalCount) {
        toastStore.success(
          $_('pages.bin.restore_success', {
            values: { restoredCount: result.restoredCount, totalCount: result.totalCount },
          }),
        );
      } else {
        toastStore.warning(
          $_('pages.bin.restore_partial_success', {
            values: { restoredCount: result.restoredCount, totalCount: result.totalCount },
          }),
        );
      }
      await load();
    } catch {
      toastStore.error($_('pages.bin.restore_error'));
    }
  }
</script>

<svelte:window on:keydown|capture={handleWindowKeydown} />

<PageHeader title={$_('pages.bin.title')} />

{#if hasMore}
  <div
    data-testid="bin-hasmore-banner"
    class="mb-4 p-3 bg-accent/10 border border-accent/20 rounded-lg text-sm text-text"
  >
    {$_('pages.bin.has_more_notice')}
  </div>
{/if}

<div class="bg-surface border border-border rounded-lg overflow-hidden">
  <FilterToolbar
    {availableFilters}
    activeFilters={filterState as Record<string, FilterValue>}
    on:filterChange={handleFilterChange}
    on:filterRemove={handleFilterRemove}
    on:clearAll={handleClearAll}
  />

  {#if loading}
    <div data-testid="bin-loading" class="flex justify-center py-12">
      <Spinner size="lg" label={$_('messages.loading')} />
    </div>
  {:else if error}
    <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <p class="text-sm text-text-secondary mb-4">{error}</p>
      <Button variant="primary" on:click={load}>{$_('pages.error.retry')}</Button>
    </div>
  {:else if sortedDocuments.length === 0}
    <div data-testid="bin-empty-state">
      <EmptyState
        title={$_('pages.bin.empty_title')}
        description={$_('pages.bin.empty_description')}
      />
    </div>
  {:else}
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-border">
            <th class="px-2 py-3 w-8 text-center">
              <input
                type="checkbox"
                data-testid="bin-select-all"
                checked={allVisibleSelected}
                on:change={handleSelectAll}
                class="accent-accent cursor-pointer"
                aria-label={$_('table.select_all_on_page')}
              />
            </th>
            <TableSortHeader
              label={$_('pages.bin.th_subject')}
              sortKey="subject"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              on:sort={handleSort}
            />
            <TableSortHeader
              label={$_('pages.bin.th_reference_date')}
              sortKey="referenceDate"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              thClass="hidden sm:table-cell"
              on:sort={handleSort}
            />
            <TableSortHeader
              label={$_('pages.bin.th_uploaded_at')}
              sortKey="uploadedAt"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              thClass="hidden lg:table-cell"
              on:sort={handleSort}
            />
            <TableSortHeader
              label={$_('pages.bin.th_merchant')}
              sortKey="merchant"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              thClass="hidden md:table-cell"
              on:sort={handleSort}
            />
            <TableSortHeader
              label={$_('pages.bin.th_amount')}
              sortKey="amount"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              thClass="hidden md:table-cell"
              on:sort={handleSort}
            />
            <TableSortHeader
              label={$_('pages.bin.th_category')}
              sortKey="category"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              thClass="hidden lg:table-cell"
              on:sort={handleSort}
            />
            <TableSortHeader
              label={$_('pages.bin.th_deleted_at')}
              sortKey="deletedAt"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              on:sort={handleSort}
            />
          </tr>
        </thead>
        <tbody>
          {#each sortedDocuments as doc (doc.id)}
            <tr
              data-testid={`bin-row-${doc.id}`}
              class="border-b border-border/50 hover:bg-surface-hover transition-colors"
            >
              <td class="px-2 py-3 w-8 text-center" on:click|stopPropagation>
                <label class="flex items-center justify-center cursor-pointer p-2 -m-2">
                  <input
                    type="checkbox"
                    data-testid={`bin-checkbox-${doc.id}`}
                    checked={selectedIds.has(doc.id)}
                    on:change={(e) => handleRowToggle(doc.id, e)}
                    class="accent-accent cursor-pointer"
                    aria-label={doc.subject ?? doc.id}
                  />
                </label>
              </td>
              <td class="px-4 py-3 max-w-[300px]">
                <a
                  href={getDocumentUrl(doc)}
                  class="text-sm font-medium text-text no-underline truncate block"
                  on:click={(e) => handleRowLinkClick(e, doc)}
                >
                  {doc.subject ?? '—'}
                </a>
              </td>
              <td class="px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                <span class="text-sm text-text">
                  {doc.referenceDate ? formatDate(doc.referenceDate, $userSettings) : '—'}
                </span>
              </td>
              <td class="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                <span class="text-sm text-text-muted">
                  {formatDate(doc.uploadedAt, $userSettings)}
                </span>
              </td>
              <td class="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                <span class="text-sm text-text-muted">{doc.merchant ?? '—'}</span>
              </td>
              <td class="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                <span class="text-sm text-text-muted">{doc.amount != null ? doc.amount : '—'}</span>
              </td>
              <td class="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                <span class="text-sm text-text-muted">{doc.category ?? '—'}</span>
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <span class="text-sm text-text">
                  {doc.deletedAt ? formatDate(doc.deletedAt, $userSettings) : '—'}
                </span>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

{#if selectedIds.size > 0}
  <div
    data-testid="bin-selection-bar"
    class="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-2.5 bg-surface border border-border rounded-full shadow-lg"
  >
    <span class="text-sm font-medium text-text whitespace-nowrap">
      {$_('pages.bin.selected_count', { values: { count: selectedIds.size } })}
    </span>
    <button
      type="button"
      data-testid="bin-bar-restore"
      on:click={handleRestore}
      class="flex items-center gap-2 px-3 py-1.5 bg-accent text-on-accent rounded-full text-sm font-semibold hover:bg-accent-hover transition-colors whitespace-nowrap"
    >
      {$_('pages.bin.restore_button')}
    </button>
  </div>
{/if}
